import "./UserMenu.styles.scss";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useLogout,
  useClickOutside,
  useGetTherapistProfile,
  useCurrentUser,
  useSwitchOrganization,
} from "utils/hooks";
import { Divider, Icon } from "components";
import DownArrowIcon from "assets/icons/arrow-down-thin.svg";
import SignOutIcon from "assets/icons/sign-out.svg";
import SettingsIcon from "assets/icons/cog.svg";
import BuildingIcon from "assets/icons/building.svg";
import CheckIcon from "assets/icons/check.svg";
import { User } from "types";
import { useNavigate } from "react-router-dom";
import { truncateText } from "utils/helpers";

interface UserMenuProps {
  user: User;
}

interface UserMenuBodyProps {
  isTherapist: boolean;
  closeMenu: () => void;
}

const UserMenuTitle: React.FC<UserMenuProps> = ({ user }) => {
  const { t } = useTranslation(["common"]);

  return (
    <div className="user-menu__title">
      <div className="user-menu__title-main">
        {t("dashboard.calm_greeting", {
          userName: user?.firstName ?? user.email,
        })}
      </div>
      <Icon src={DownArrowIcon} />
    </div>
  );
};

const UserMenuBody: React.FC<UserMenuBodyProps> = ({
  isTherapist,
  closeMenu,
}) => {
  const { logOut } = useLogout();
  const { t } = useTranslation(["common"]);
  const { isTherapistProfileComplete } = useGetTherapistProfile();
  const showTherapistProfileAlert = isTherapist && !isTherapistProfileComplete;
  const { user } = useCurrentUser();
  const organizations = user?.organizations || [];
  const { switchOrganization, isSwitching } = useSwitchOrganization();

  const navigate = useNavigate();

  const handleNavigation = (path: string) => {
    navigate(path);
    closeMenu();
  };

  const handleLogout = () => {
    logOut();
    closeMenu();
  };

  const handleOrganizationSwitch = (organizationId: string) => {
    switchOrganization(organizationId);
    closeMenu();
  };

  // Only show organization switcher if user has more than 1 organization
  const showOrganizationSwitcher = organizations.length > 1;

  return (
    <ul className="user-menu__list">
      <li className="user-menu__list-item">
        <button
          className="user-menu__button"
          onClick={() => handleNavigation("/settings")}
          disabled={isSwitching}
        >
          {showTherapistProfileAlert && <div className="user-menu-item__dot" />}
          <div className="user-menu-item__title">
            {t("page_titles.settings_title")}
          </div>
          <div className="user-menu-item__icon">
            <Icon src={SettingsIcon} />
          </div>
        </button>
      </li>
      <Divider small />
      {showOrganizationSwitcher && (
        <>
          {organizations.map((org) => {
            const isActive = org.organizationId === user?.activeOrganizationId;
            return (
              <li key={org.organizationId} className="user-menu__list-item">
                <button
                  className={`user-menu__button ${
                    isActive ? "user-menu__button--active" : ""
                  } ${isSwitching ? "user-menu__button--loading" : ""}`}
                  onClick={() => handleOrganizationSwitch(org.organizationId)}
                  disabled={isSwitching}
                >
                  <div className="user-menu-item__title">
                    {truncateText(org.name, 25)}
                    {isActive && (
                      <span className="user-menu-item__active-label">
                        {" "}
                        (Active)
                      </span>
                    )}
                    {isSwitching && (
                      <span className="user-menu-item__loading-label">
                        {" "}
                        (Switching...)
                      </span>
                    )}
                  </div>
                  <div className="user-menu-item__icon">
                    {isSwitching ? (
                      <div className="user-menu-item__spinner" />
                    ) : isActive ? (
                      <Icon src={CheckIcon} />
                    ) : (
                      <Icon src={BuildingIcon} />
                    )}
                  </div>
                </button>
              </li>
            );
          })}
          <Divider small />
        </>
      )}
      <li className="user-menu__list-item">
        <button
          className="user-menu__button"
          onClick={handleLogout}
          disabled={isSwitching}
        >
          <div className="user-menu-item__title">
            {t("page_titles.logout_title")}
          </div>
          <div className="user-menu-item__icon">
            <Icon src={SignOutIcon} />
          </div>
        </button>
      </li>
    </ul>
  );
};

const UserMenu: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { node, isNodeVisible, setIsNodeVisible } = useClickOutside();
  const { user, isTherapist } = useCurrentUser();

  const toggleMenu = () => {
    const nextState = !isMenuOpen;
    setIsNodeVisible(nextState);
    setIsMenuOpen(nextState);
  };

  const closeMenu = () => {
    setIsNodeVisible(false);
    setIsMenuOpen(false);
  };

  // Handle closing the menu when clicking outside
  React.useEffect(() => {
    if (!isNodeVisible && isMenuOpen) {
      setIsMenuOpen(false);
    }
  }, [isMenuOpen, isNodeVisible]);

  if (!user) {
    return null;
  }

  return (
    <div className="user-menu" ref={node}>
      <button onClick={toggleMenu} className="user-menu__header">
        <UserMenuTitle user={user} />
      </button>
      {isMenuOpen && (
        <div className="user-menu__dropdown">
          <UserMenuBody isTherapist={isTherapist} closeMenu={closeMenu} />
        </div>
      )}
    </div>
  );
};

export default UserMenu;
